import React, { FC, useMemo, useState } from 'react';
import type { ColumnsType } from 'antd/lib/table';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ITableRow } from '../../../services/dashboard';
import { RouteNames } from '../../../constans/routes';
import Input from '../../../shared/Input/Input';
import filterDepartmentRows from './filterDepartmentRows';
import {
  DepartmentsCont,
  ReportNo,
  ReportYes,
  SearchCont,
  StyledDepartmentsTable,
  TableData,
  TableHeader,
} from './Departments.styles';

interface IDepartments {
  tableRows: ITableRow[];
  isEffect?: boolean;
  surveyType?: string;
}

const sortDirs: ['ascend', 'descend'] = ['ascend', 'descend'];

const Departments: FC<IDepartments> = ({ tableRows, isEffect = false, surveyType = '' }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchPhrase, setSearchPhrase] = useState('');

  const filteredRows = useMemo(
    () =>
      filterDepartmentRows(tableRows, searchPhrase, {
        isEffect,
        reportLabels: {
          yes: t('viewSurvey.dashboard.departments.report.yes'),
          no: t('viewSurvey.dashboard.departments.report.no'),
        },
      }),
    [tableRows, searchPhrase, isEffect, t]
  );

  const renderHeader = (key: string) => (
    <TableHeader>{t(`viewSurvey.dashboard.departments.columns.${key}`)}</TableHeader>
  );

  const columns: ColumnsType<ITableRow> = [
    {
      title: renderHeader('department'),
      dataIndex: 'name',
      key: 'name',
      sortDirections: sortDirs,
      sorter: (a, b) => a.name.localeCompare(b.name),
      render: (val: string) => <TableData>{val}</TableData>,
    },
    {
      title: renderHeader('report'),
      dataIndex: 'report_exist',
      key: 'report_exist',
      render: (val: boolean) =>
        val ? (
          <ReportYes>{t('viewSurvey.dashboard.departments.report.yes')}</ReportYes>
        ) : (
          <ReportNo>{t('viewSurvey.dashboard.departments.report.no')}</ReportNo>
        ),
    },
    {
      title: renderHeader('invited'),
      dataIndex: 'invited',
      key: 'invited',
      sortDirections: sortDirs,
      sorter: (a, b) => a.invited - b.invited,
      render: (val: number) => <TableData>{val}</TableData>,
    },
    {
      title: renderHeader('completed'),
      dataIndex: 'completed',
      key: 'completed',
      sortDirections: sortDirs,
      sorter: (a, b) => a.completed - b.completed,
      render: (val: number, record: ITableRow) => (
        <TableData>
          {val} ({record.completed_ratio}%)
        </TableData>
      ),
    },
    {
      title: renderHeader('notAnswered'),
      dataIndex: 'not_completed',
      key: 'not_completed',
      sortDirections: sortDirs,
      sorter: (a, b) => a.not_completed - b.not_completed,
      render: (val: number, record: ITableRow) => (
        <TableData>
          {val} ({record.not_completed_ratio}%)
        </TableData>
      ),
    },
    ...(isEffect
      ? [
          {
            title: renderHeader('internalCompletedPct'),
            dataIndex: 'internal_completed_ratio',
            key: 'internal_completed_ratio',
            sortDirections: sortDirs,
            sorter: (a: ITableRow, b: ITableRow) =>
              (a.internal_completed_ratio ?? 0) - (b.internal_completed_ratio ?? 0),
            render: (val: number) => <TableData>{val ?? '-'}</TableData>,
          },
          {
            title: renderHeader('externalCompletedPct'),
            dataIndex: 'external_completed_ratio',
            key: 'external_completed_ratio',
            sortDirections: sortDirs,
            sorter: (a: ITableRow, b: ITableRow) =>
              (a.external_completed_ratio ?? 0) - (b.external_completed_ratio ?? 0),
            render: (val: number) => <TableData>{val ?? '-'}</TableData>,
          },
        ]
      : []),
  ];

  return (
    <DepartmentsCont>
      <SearchCont>
        <Input
          value={searchPhrase}
          onChange={setSearchPhrase}
          placeholder={t('viewSurvey.dashboard.departments.search')}
        />
      </SearchCont>
      <StyledDepartmentsTable<ITableRow>
        dataSource={filteredRows}
        columns={columns}
        rowKey="id"
        pagination={false}
        showSorterTooltip={false}
        scroll={{ y: 500 }}
        onRow={record => ({
          onClick: record.report_exist
            ? () => navigate(`${RouteNames.Reports}/${record.id}?surveyType=${surveyType}`)
            : undefined,
          style: { cursor: record.report_exist ? 'pointer' : 'default' },
        })}
      />
    </DepartmentsCont>
  );
};

export default Departments;
